import { Injectable, signal, inject } from '@angular/core';
import { ApiUrl, IrisApiClient, IrisQueryParams } from '@iris/api-query';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { IrisFieldsTranslationSet } from '@iris/common/models';
import { IrisUserService } from '@iris/common/services/user.service';

export interface GlobalNotificationI {
  /**
   * readonly because they're not used
   */
  readonly instanceId?: string;
  readonly title?: string;
  readonly titleTranslations?: Record<string, unknown>;

  id?: number;
  message: string;
  messageTranslations?: Record<string, unknown>;
  active: boolean;
}

@Injectable({ providedIn: 'root' })
@ApiUrl('/notifications/{iris@instanceId}/global')
export class IrisGlobalNotificationService extends IrisApiClient<GlobalNotificationI> {
  protected readonly httpClient: HttpClient;
  protected readonly userService = inject(IrisUserService);

  notificationSubject = new BehaviorSubject<GlobalNotificationI>(null);
  notification$ = this.notificationSubject.asObservable();
  notificationActive = signal(false);
  notificationMessage = toSignal(
    this.notification$.pipe(
      map((notification: GlobalNotificationI) => {
        this.notificationActive.set(notification?.active || false);
        const lang = this.userService.me?.language || 'en';
        const translations = notification?.messageTranslations as IrisFieldsTranslationSet;

        return translations?.[lang]?.message || notification?.message || '';
      }),
      takeUntilDestroyed(),
    ),
  );

  constructor() {
    const httpClient = inject(HttpClient);

    super(httpClient);
  
    this.httpClient = httpClient;
  }

  query(queryParams?: IrisQueryParams): Observable<GlobalNotificationI[]> {
    return super.queryCore(queryParams);
  }

  post(notification: GlobalNotificationI): Observable<GlobalNotificationI> {
    return super.saveCore(notification).pipe(
      tap(() => { this.notificationSubject.next(notification); }),
    );
  }

  @ApiUrl(`~/active`)
  getActive(): Observable<GlobalNotificationI> {
    return this.httpClient.get<GlobalNotificationI>(this.url()).pipe(
      tap((notification) => { this.notificationSubject.next(notification); }),
    );
  }

}
